import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { saveBonusToReports } from '../utils/bonusTracker';

const CartellaCheckModal = ({
  open,
  onClose,
  cartella,
  cartellaNumber,
  drawnNumbers,
  winningPattern,
  isWinner,
  onAdditional,
  onNewBingo,
  cheersNumbers,
}) => {
  const [selectedBonus, setSelectedBonus] = useState("");
  const [bonusResult, setBonusResult] = useState(null);
  const [showBonusSection, setShowBonusSection] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [winningLines, setWinningLines] = useState([]);
  const hasFoundWinnerRef = useRef(false);
  const hasPlayedPassedRef = useRef(false);
  const goodAudioRef = useRef();
  const notGoodAudioRef = useRef();

  // Reset bonus result when modal opens/closes or cartella changes
  useEffect(() => {
    setSelectedBonus("");
    setBonusResult(null);
    setWinningLines([]); // Reset winning lines when modal opens or cartella changes
    hasFoundWinnerRef.current = false; // Reset the latch
    hasPlayedPassedRef.current = false; // Reset passed sound flag
  }, [open, cartella?.id]); // Also reset when cartella changes

  // Add keyboard shortcut for test mode (Alt+B)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // Alt+B to toggle test mode (B for Bonus)
      if (e.altKey && e.key === "b") {
        setTestMode((prev) => !prev);
        
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, testMode]);

  useEffect(() => {
    goodAudioRef.current = new Audio("/sounds/effects/good.wav");
    notGoodAudioRef.current = new Audio("/sounds/effects/notgood.wav");
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 600,
    bgcolor: "white",
    boxShadow: 24,
    p: 2,
    borderRadius: 1,
    outline: "none",
    alignItems: "center",
    maxHeight: "95vh",
    overflowY: showBonusSection ? "auto" : "hidden",
    overflowX: "hidden",
    opacity: 0.9,
    tabIndex: -1,
  };

  useEffect(() => {
    // Debug logging when props change
  
  }, [cartellaNumber, drawnNumbers, winningPattern, isWinner, cartella]);

  const handleGoodBingo = async () => {
    const audio = goodAudioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio
        .play()
        .catch((error) => console.error("Error playing sound:", error));
    }
    
    // Save bonus if one was awarded
    if (bonusResult && bonusResult.success) {
      try {
       
        await saveBonusToReports({
          bonusType: selectedBonus === 'anyOneLine' ? 'አንድ ዝግ' : 
                     selectedBonus === 'anyTwoLines' ? 'ሁለት ዝግ' : 'ትርስ',
          bonusAmount: bonusResult.prize,
          bonusDetails: {
            type: selectedBonus,
            callsCount: drawnNumbers?.length || 0,
            cartellaNumber: cartellaNumber || (testMode ? 'TEST-001' : 'UNKNOWN'),
            testMode: testMode,
            timestamp: new Date().toISOString()
          }
        });
       
      } catch (error) {
        console.error('❌ Failed to save bonus:', error);
      }
    }
    
    // Confetti removed for performance
  };

  const handleNotBingo = () => {
    const audio = notGoodAudioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio
        .play()
        .catch((error) => console.error("Error playing sound:", error));
    }
  };

  // Check for bonus
  const handleCheckBonus = () => {
    setIsLoading(true);
    // Simulate network delay
    setTimeout(async () => {
      await checkBonus();
      setIsLoading(false);
    }, 500); // 0.5 second delay
  };

  const checkBonus = async () => {
    if (!selectedBonus || !cartella || !cartella.numbers) {
      setBonusResult(null);
      return;
    }

    // If test mode is enabled, we'll simulate a winning condition
    if (testMode) {


      // Simulate different bonus types and SAVE them to reports for testing
      switch (selectedBonus) {
        case "anyOneLine":
          const oneLinePrize = 2000;
          setBonusResult({
            success: true,
            message: "አንድ ዝግ ተሸላሚ ነዎት! (TEST MODE)",
            details: `Row 1 is complete within ${drawnNumbers.length} calls. (TEST MODE)`,
            prize: oneLinePrize,
          });
          
          // Bonus will be saved when user clicks "GOOD BINGO"
          return;

        case "anyTwoLines":
          const twoLinesPrize = 1000; // Corrected prize for two lines
          setBonusResult({
            success: true,
            message: "ሁለት ዝግ ተሸላሚ ነዎት! (TEST MODE)",
            details: `Two lines completed within ${drawnNumbers.length} calls. (TEST MODE)`,
            prize: twoLinesPrize,
          });
          
          // Bonus will be saved when user clicks "GOOD BINGO"
          return;

        case "cheers":
          const cheersPrize = 100; // Corrected prize for CHEERS
          setBonusResult({
            success: true,
            message: "ትርስ ተሸላሚ ነዎት! (TEST MODE)",
            details: `All CHEERS numbers found! (TEST MODE)`,
            prize: cheersPrize,
          });
          
          // Bonus will be saved when user clicks "GOOD BINGO"
          return;
      }
    }

    switch (selectedBonus) {
      case "anyOneLine": {
        // Check for any one line (row, column, or diagonal)
        // Check rows
        for (let i = 0; i < 5; i++) {
          const row = cartella.numbers[i];
          if (
            row.every(
              (num) => num === "free" || drawnNumbers.includes(parseInt(num))
            )
          ) {
            const prize = drawnNumbers.length <= 4 ? 2000 : drawnNumbers.length <= 5 ? 200 : drawnNumbers.length <= 6 ? 100 : 0;
            
            setBonusResult({
              success: true,
              message: "አንድ ዝግ ተሸላሚ ነዎት!",
              details: `Row ${i + 1} is complete within ${drawnNumbers.length} calls.`,
              prize: prize,
            });
            
            // Save bonus to reports if prize > 0
            if (prize > 0) {
              try {
                await saveBonusToReports({
                  bonusType: "አንድ ዝግ",
                  bonusAmount: prize,
                  bonusDetails: {
                    type: "row",
                    lineNumber: i + 1,
                    callsCount: drawnNumbers.length,
                    cartellaNumber: cartellaNumber,
                    completedNumbers: cartella.numbers[i].filter(num => num !== "free")
                  }
                });
               
              } catch (error) {
                console.error('❌ Failed to save bonus:', error);
              }
            }
            return;
          }
        }

        // Check columns
        for (let j = 0; j < 5; j++) {
          const column = cartella.numbers.map((row) => row[j]);
          if (
            column.every(
              (num) => num === "free" || drawnNumbers.includes(parseInt(num))
            )
          ) {
            const prize = drawnNumbers.length <= 4 ? 2000 : drawnNumbers.length <= 5 ? 200 : drawnNumbers.length <= 6 ? 100 : 0;
            
            setBonusResult({
              success: true,
              message: "አንድ ዝግ ተሸላሚ ነዎት!",
              details: `Column ${j + 1} is complete within ${drawnNumbers.length} calls.`,
              prize: prize,
            });
            
            // Save bonus to reports if prize > 0
            if (prize > 0) {
              try {
                await saveBonusToReports({
                  bonusType: "አንድ ዝግ",
                  bonusAmount: prize,
                  bonusDetails: {
                    type: "column",
                    lineNumber: j + 1,
                    callsCount: drawnNumbers.length,
                    cartellaNumber: cartellaNumber,
                    completedNumbers: cartella.numbers.map(row => row[j]).filter(num => num !== "free")
                  }
                });
               
              } catch (error) {
                console.error('❌ Failed to save bonus:', error);
              }
            }
            return;
          }
        }

        // Check diagonals
        const mainDiagonal = cartella.numbers.map((row, i) => row[i]);
        if (
          mainDiagonal.every(
            (num) => num === "free" || drawnNumbers.includes(parseInt(num))
          )
        ) {
          const prize = drawnNumbers.length <= 4 ? 2000 : drawnNumbers.length <= 5 ? 200 : drawnNumbers.length <= 6 ? 100 : 0;
          
          setBonusResult({
            success: true,
            message: "አንድ ዝግ ተሸላሚ ነዎት!",
            details: `Main diagonal is complete within ${drawnNumbers.length} calls.`,
            prize: prize,
          });
          
          // Save bonus to reports if prize > 0
          if (prize > 0) {
            try {
              await saveBonusToReports({
                bonusType: "አንድ ዝግ",
                bonusAmount: prize,
                bonusDetails: {
                  type: "mainDiagonal",
                  callsCount: drawnNumbers.length,
                  cartellaNumber: cartellaNumber,
                  completedNumbers: mainDiagonal.filter(num => num !== "free")
                }
              });
       
            } catch (error) {
              console.error('❌ Failed to save bonus:', error);
            }
          }
          return;
        }

        const antiDiagonal = cartella.numbers.map((row, i) => row[4 - i]);
        if (
          antiDiagonal.every(
            (num) => num === "free" || drawnNumbers.includes(parseInt(num))
          )
        ) {
          const prize = drawnNumbers.length <= 4 ? 2000 : drawnNumbers.length <= 5 ? 200 : drawnNumbers.length <= 6 ? 100 : 0;
          
          setBonusResult({
            success: true,
            message: "አንድ ዝግ ተሸላሚ ነዎት!",
            details: `Anti-diagonal is complete within ${drawnNumbers.length} calls.`,
            prize: prize,
          });
          
          // Save bonus to reports if prize > 0
          if (prize > 0) {
            try {
              await saveBonusToReports({
                bonusType: "አንድ ዝግ",
                bonusAmount: prize,
                bonusDetails: {
                  type: "antiDiagonal",
                  callsCount: drawnNumbers.length,
                  cartellaNumber: cartellaNumber,
                  completedNumbers: antiDiagonal.filter(num => num !== "free")
                }
              });
             
            } catch (error) {
              console.error('❌ Failed to save bonus:', error);
            }
          }
          return;
        }

        // No line found
        setBonusResult({
          success: false,
          message: "ይቅርታ፣ አንድ ዝግ አላገኙም።",
          details: "No complete line found.",
          prize: 0,
        });
        break;
      }

      case "anyTwoLines": {
        // Check for any two lines (rows, columns, or diagonals)
        let completedLines = [];

        // Check rows
        for (let i = 0; i < 5; i++) {
          const row = cartella.numbers[i];
          if (
            row.every(
              (num) => num === "free" || drawnNumbers.includes(parseInt(num))
            )
          ) {
            completedLines.push(`Row ${i + 1}`);
          }
        }

        // Check columns
        for (let j = 0; j < 5; j++) {
          const column = cartella.numbers.map((row) => row[j]);
          if (
            column.every(
              (num) => num === "free" || drawnNumbers.includes(parseInt(num))
            )
          ) {
            completedLines.push(`Column ${j + 1}`);
          }
        }

        // Check diagonals
        const mainDiagonal = cartella.numbers.map((row, i) => row[i]);
        if (
          mainDiagonal.every(
            (num) => num === "free" || drawnNumbers.includes(parseInt(num))
          )
        ) {
          completedLines.push("Main diagonal");
        }

        const antiDiagonal = cartella.numbers.map((row, i) => row[4 - i]);
        if (
          antiDiagonal.every(
            (num) => num === "free" || drawnNumbers.includes(parseInt(num))
          )
        ) {
          completedLines.push("Anti-diagonal");
        }

        if (completedLines.length >= 2) {
          const prize = drawnNumbers.length <= 11 ? 1000 : drawnNumbers.length <= 12 ? 500 : drawnNumbers.length <= 13 ? 300 : drawnNumbers.length <= 14 ? 200 : drawnNumbers.length <= 15 ? 100 : 0;
          
          setBonusResult({
            success: true,
            message: "ሁለት ዝግ ተሸላሚ ነዎት!",
            details: `${completedLines
              .slice(0, 2)
              .join(" and ")} are complete within ${
              drawnNumbers.length
            } calls.`,
            prize: prize,
          });
          
          // Save bonus to reports if prize > 0
          if (prize > 0) {
            try {
              await saveBonusToReports({
                bonusType: "ሁለት ዝግ",
                bonusAmount: prize,
                bonusDetails: {
                  completedLines: completedLines.slice(0, 2),
                  callsCount: drawnNumbers.length,
                  cartellaNumber: cartellaNumber,
                  totalLinesFound: completedLines.length
                }
              });
          
            } catch (error) {
              console.error('❌ Failed to save bonus:', error);
            }
          }
        } else {
          setBonusResult({
            success: false,
            message: "ይቅርታ፣ ሁለት ዝግ አላገኙም።",
            details: `Only ${completedLines.length} line(s) found: ${
              completedLines.join(", ") || "None"
            }.`,
            prize: 0,
          });
        }
        break;
      }

      case "cheers": {
        // Check if cartella contains all 5 specific CHEERS numbers
        if (!cheersNumbers || cheersNumbers.length !== 5) {
          setBonusResult({
            success: false,
            message: "ይቅርታ፣ የቺርስ ቁጥሮች አልተገኙም።",
            details: "CHEERS numbers not available.",
            prize: 0,
          });
          return;
        }

        // Extract the actual numbers from the cheersNumbers objects
        const targetNumbers = cheersNumbers.map((item) => item.number);

        // Check if all target numbers are in the cartella and have been drawn
        let foundNumbers = [];
        let notFoundNumbers = [];

        for (const targetNum of targetNumbers) {
          let found = false;

          // Check if number is in cartella and has been drawn
          for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 5; j++) {
              const num = cartella.numbers[i][j];
              if (
                num !== "free" &&
                parseInt(num) === targetNum &&
                drawnNumbers.includes(targetNum)
              ) {
                found = true;
                foundNumbers.push(targetNum);
                break;
              }
            }
            if (found) break;
          }

          if (!found) {
            notFoundNumbers.push(targetNum);
          }
        }

        if (foundNumbers.length === 5) {
          const prize = 100;
          
          setBonusResult({
            success: true,
            message: "ቺርስ ተሸላሚ ነዎት!",
            details: "All 5 CHEERS numbers found in your cartella!",
            prize: prize,
          });
          
          // Save bonus to reports
          try {
            await saveBonusToReports({
              bonusType: "ቺርስ",
              bonusAmount: prize,
              bonusDetails: {
                foundNumbers: foundNumbers,
                cheersNumbers: targetNumbers,
                cartellaNumber: cartellaNumber,
                callsCount: drawnNumbers.length
              }
            });
           
          } catch (error) {
            console.error('❌ Failed to save bonus:', error);
          }
        } else {
          setBonusResult({
            success: false,
            message: "ይቅርታ፣ ቺርስ አላገኙም።",
            details: `Found ${
              foundNumbers.length
            }/5 CHEERS numbers. Missing: ${notFoundNumbers.join(", ")}.`,
            prize: 0,
          });
        }
        break;
      }

      default:
        setBonusResult(null);
    }
  };

  // Helper function to check if a number is drawn
  const isNumberDrawn = (num) => {
    // Make the free check case-insensitive
    if (String(num).toLowerCase() === "free" || num === "0" || num === 0)
      return true;

    // Convert both the number and drawn numbers to numbers for comparison
    const numValue = parseInt(num);
    if (isNaN(numValue)) return false;

    const result = drawnNumbers.includes(numValue);
  
    return result;
  };

  // Helper function to check if a line is complete
  const isLineComplete = (numbers) => {
    const result = numbers.every(isNumberDrawn);
  
    return result;
  };

  // Effect to detect and store the first winning line(s)
  useEffect(() => {
    if (!open || !cartella || !winningPattern || hasFoundWinnerRef.current)
      return;

    const findWinningLines = () => {
      const lines = [];
      // Using a function to avoid re-checking once enough lines are found
      const checkAndPush = (line) => {
        if (winningPattern === "Any one Line" && lines.length >= 1) return;
        if (winningPattern === "Any two Lines" && lines.length >= 2) return;
        lines.push(line);
      };

      if (
        winningPattern === "Any one Line" ||
        winningPattern === "Any two Lines"
      ) {
        // Priority 1: Rows
        for (let i = 0; i < 5; i++) {
          if (cartella.numbers[i].every(isNumberDrawn))
            checkAndPush({ type: "row", index: i });
        }
        // Priority 2: Columns
        for (let j = 0; j < 5; j++) {
          if (cartella.numbers.map((row) => row[j]).every(isNumberDrawn))
            checkAndPush({ type: "column", index: j });
        }
        // Priority 3: Diagonals
        if (cartella.numbers.map((row, i) => row[i]).every(isNumberDrawn))
          checkAndPush({ type: "mainDiagonal" });
        if (cartella.numbers.map((row, i) => row[4 - i]).every(isNumberDrawn))
          checkAndPush({ type: "antiDiagonal" });
        // Priority 4: Four corners
        const fourCorners = [
          cartella.numbers[0][0], // Top-left
          cartella.numbers[0][4], // Top-right
          cartella.numbers[4][0], // Bottom-left
          cartella.numbers[4][4]  // Bottom-right
        ];
        if (fourCorners.every(isNumberDrawn))
          checkAndPush({ type: "fourCorners" });
      }
      return lines;
    };

    const foundLines = findWinningLines();
    let winnerFound = false;

    if (winningPattern === "Any one Line" && foundLines.length >= 1) {
      setWinningLines([foundLines[0]]);
      winnerFound = true;
    } else if (winningPattern === "Any two Lines" && foundLines.length >= 2) {
      setWinningLines(foundLines.slice(0, 2));
      winnerFound = true;
    }

    if (winnerFound) {
      hasFoundWinnerRef.current = true;
      
      // Check if winning line contains last drawn number
      const lastDrawnNumber = drawnNumbers[drawnNumbers.length - 1];
      const winningLine = foundLines[0];
      
      // Get numbers from the winning line
      let lineNumbers = [];
      if (winningLine.type === "row") {
        lineNumbers = cartella.numbers[winningLine.index];
      } else if (winningLine.type === "column") {
        lineNumbers = cartella.numbers.map(row => row[winningLine.index]);
      } else if (winningLine.type === "mainDiagonal") {
        lineNumbers = cartella.numbers.map((row, i) => row[i]);
      } else if (winningLine.type === "antiDiagonal") {
        lineNumbers = cartella.numbers.map((row, i) => row[4 - i]);
      } else if (winningLine.type === "fourCorners") {
        lineNumbers = [cartella.numbers[0][0], cartella.numbers[0][4], cartella.numbers[4][0], cartella.numbers[4][4]];
      }
      
      // Check if last drawn number is in the winning line
      const hasLastNumber = lineNumbers.some(num => {
        if (String(num).toLowerCase() === "free") return false;
        return parseInt(num) === parseInt(lastDrawnNumber);
      });
      
      // If winning line doesn't have last number, play passed sound immediately (only once)
      if (!hasLastNumber && !hasPlayedPassedRef.current) {
        hasPlayedPassedRef.current = true;
        const passedAudio = new Audio("/sounds/effects/passed.wav");
        passedAudio.play().catch((error) => console.error("Error playing passed sound:", error));
      }
    }
  }, [drawnNumbers, open, cartella, winningPattern]);

  // Function to check if a number is part of the winning pattern
  const isWinningNumber = (number, rowIndex, colIndex) => {
    // Remove the isWinner check - we want to show the pattern even if isWinner is false
    if (!winningPattern) return false;

    const isMiddleCell = rowIndex === 2 && colIndex === 2;
  

    switch (winningPattern) {
      case "Any one Line":
      case "Any two Lines": {
        if (winningLines.length === 0) return false;
        // Check if the current cell is part of any of the stored winning lines
        return winningLines.some((line) => {
          switch (line.type) {
            case "row":
              return rowIndex === line.index;
            case "column":
              return colIndex === line.index;
            case "mainDiagonal":
              return rowIndex === colIndex;
            case "antiDiagonal":
              return rowIndex + colIndex === 4;
            case "fourCorners":
              return (rowIndex === 0 && colIndex === 0) || // Top-left
                     (rowIndex === 0 && colIndex === 4) || // Top-right
                     (rowIndex === 4 && colIndex === 0) || // Bottom-left
                     (rowIndex === 4 && colIndex === 4);   // Bottom-right
            default:
              return false;
          }
        });
      }

      case "Any Horizontal": {
        // Debug current cell
       

        // Find the first complete horizontal line (row)
        let winningRow = null;
        for (let i = 0; i < 5; i++) {
          const row = cartella.numbers[i];
          if (row.every(isNumberDrawn)) {
            winningRow = i;
            break;
          }
        }

      

        // Return true only if current cell is in the first winning row
        return winningRow !== null && rowIndex === winningRow;
      }

      case "Any Vertical": {
        // Debug current cell
        

        // Find the first complete vertical line (column)
        let winningColumn = null;
        for (let j = 0; j < 5; j++) {
          const column = cartella.numbers.map((row) => row[j]);
          if (column.every(isNumberDrawn)) {
            winningColumn = j;
            break;
          }
        }

        // Return true only if current cell is in the first winning column
        return winningColumn !== null && colIndex === winningColumn;
      }

      case "T Pattern": {
        // Debug the pattern check
        

        // First check if cell is in middle row or column
        const isInPattern = rowIndex === 0 || colIndex === 2;
        if (!isInPattern) {
    
          return false;
        }

        // Get all numbers in middle row and column
        const topRow = cartella.numbers[0];
        const middleColumn = cartella.numbers.map((row) => row[2]);

        const topRowComplete = topRow.every(isNumberDrawn);
        const middleColumnComplete = middleColumn.every(isNumberDrawn);

       

        return topRowComplete && middleColumnComplete && isInPattern;
      }

      case "Reverse T": {
        // Debug the pattern check
        

        // First check if cell is in bottom row or middle column
        const isInPattern = rowIndex === 4 || colIndex === 2;
        if (!isInPattern) {
       
          return false;
        }

        // Get all numbers in bottom row and middle column
        const bottomRow = cartella.numbers[4];
        const middleColumn = cartella.numbers.map((row) => row[2]);

        const bottomRowComplete = bottomRow.every(isNumberDrawn);
        const middleColumnComplete = middleColumn.every(isNumberDrawn);

       

        return bottomRowComplete && middleColumnComplete && isInPattern;
      }

      case "X Pattern": {
        // Debug the pattern check
       

        // First check if cell is in either diagonal
        const isInMainDiagonal = rowIndex === colIndex;
        const isInAntiDiagonal = rowIndex + colIndex === 4;
        const isInPattern = isInMainDiagonal || isInAntiDiagonal;

        if (!isInPattern) {
          
          return false;
        }

        // Get all numbers in both diagonals
        const mainDiagonal = cartella.numbers.map((row, i) => row[i]);
        const antiDiagonal = cartella.numbers.map((row, i) => row[4 - i]);

        // Check if both diagonals are complete
        const mainDiagonalComplete = mainDiagonal.every(isNumberDrawn);
        const antiDiagonalComplete = antiDiagonal.every(isNumberDrawn);

      

        // Both diagonals must be complete and cell must be in pattern
        return mainDiagonalComplete && antiDiagonalComplete && isInPattern;
      }

      case "L Pattern": {
        // Debug the pattern check
       
        // First check if cell is in bottom row or left column
        const isInPattern = rowIndex === 4 || colIndex === 0;
        if (!isInPattern) {
          
          return false;
        }

        // Get all numbers in bottom row and left column
        const bottomRow = cartella.numbers[4];
        const leftColumn = cartella.numbers.map((row) => row[0]);

        // Check if both parts are complete
        const bottomRowComplete = bottomRow.every(isNumberDrawn);
        const leftColumnComplete = leftColumn.every(isNumberDrawn);


        // Both parts must be complete and cell must be in pattern
        return bottomRowComplete && leftColumnComplete && isInPattern;
      }

      case "Reverse L": {
        // Debug the pattern check
      

        // First check if cell is in bottom row or right column
        const isInPattern = rowIndex === 4 || colIndex === 4;
        if (!isInPattern) {
 
          return false;
        }

        // Get all numbers in bottom row and right column
        const bottomRow = cartella.numbers[4];
        const rightColumn = cartella.numbers.map((row) => row[4]);

        // Check if both parts are complete
        const bottomRowComplete = bottomRow.every(isNumberDrawn);
        const rightColumnComplete = rightColumn.every(isNumberDrawn);


        // Both parts must be complete and cell must be in pattern
        return bottomRowComplete && rightColumnComplete && isInPattern;
      }

      case "Half Above": {
        // Debug the pattern check
      

        // First check if cell is in top half
        const isInPattern = rowIndex < 3;
        if (!isInPattern) {
     
          return false;
        }

        // Get all numbers in top half
        const topHalf = cartella.numbers.slice(0, 3);

        // Check if all cells in top half are drawn
        const topHalfComplete = topHalf.every((row) =>
          row.every(isNumberDrawn)
        );

      

        // Top half must be complete and cell must be in pattern
        return topHalfComplete && isInPattern;
      }

      case "Half Below": {
        // Debug the pattern check
      

        // First check if cell is in bottom half
        const isInPattern = rowIndex >= 2;
        if (!isInPattern) {
    
          return false;
        }

        // Get all numbers in bottom half
        const bottomHalf = cartella.numbers.slice(2);

        // Check if all cells in bottom half are drawn
        const bottomHalfComplete = bottomHalf.every((row) =>
          row.every(isNumberDrawn)
        );


        // Bottom half must be complete and cell must be in pattern
        return bottomHalfComplete && isInPattern;
      }

      case "Half Left": {
        // Debug the pattern check
     

        // First check if cell is in left half
        const isInPattern = colIndex < 3;
        if (!isInPattern) {
    
          return false;
        }

        // Get all numbers in left half (first 3 columns)
        const leftHalf = cartella.numbers.map((row) => row.slice(0, 3));

        // Check if all cells in left half are drawn
        const leftHalfComplete = leftHalf.every((row) =>
          row.every(isNumberDrawn)
        );

        

        // Left half must be complete and cell must be in pattern
        return leftHalfComplete && isInPattern;
      }

      case "Half Right": {
        // Debug the pattern check
       

        // First check if cell is in right half
        const isInPattern = colIndex >= 2;
        if (!isInPattern) {
         
          return false;
        }

        // Get all numbers in right half (last 3 columns)
        const rightHalf = cartella.numbers.map((row) => row.slice(2));

        // Check if all cells in right half are drawn
        const rightHalfComplete = rightHalf.every((row) =>
          row.every(isNumberDrawn)
        );

     

        // Right half must be complete and cell must be in pattern
        return rightHalfComplete && isInPattern;
      }

      case "G and O": {
        // Check if current cell is in G or O column
        if (colIndex !== 3 && colIndex !== 4) return false;

        // Check if all numbers in G and O columns are drawn
        return cartella.numbers.every(
          (row) =>
            (row[3] === "free" || drawnNumbers.includes(row[3])) &&
            (row[4] === "free" || drawnNumbers.includes(row[4]))
        );
      }

      case "B and O": {
        // Check if current cell is in B or O column
        if (colIndex !== 0 && colIndex !== 4) return false;

        // Check if all numbers in B and O columns are drawn
        return cartella.numbers.every(
          (row) =>
            (row[0] === "free" || drawnNumbers.includes(row[0])) &&
            (row[4] === "free" || drawnNumbers.includes(row[4]))
        );
      }

      case "Mark": {
        const isBorderCell =
          rowIndex === 0 || rowIndex === 4 || colIndex === 0 || colIndex === 4;

        // To check for a win, verify all border numbers are drawn
        const topRow = cartella.numbers[0];
        const bottomRow = cartella.numbers[4];
        const leftColumn = cartella.numbers.map((row) => row[0]);
        const rightColumn = cartella.numbers.map((row) => row[4]);

        const isMarkWin =
          topRow.every(isNumberDrawn) &&
          bottomRow.every(isNumberDrawn) &&
          leftColumn.every(isNumberDrawn) &&
          rightColumn.every(isNumberDrawn);

        // A cell is a winning number if it's on the border and the mark pattern has won
        return isMarkWin && isBorderCell;
      }

      case "T Cross": {
        // Debug the pattern check
      

        // First check if cell is in middle row or column
        const isInPattern = rowIndex === 2 || colIndex === 2;
        if (!isInPattern) {
       
          return false;
        }

        // Get all numbers in middle row and column
        const middleRow = cartella.numbers[2];
        const middleColumn = cartella.numbers.map((row) => row[2]);

        // Check if both parts are complete
        const middleRowComplete = middleRow.every(isNumberDrawn);
        const middleColumnComplete = middleColumn.every(isNumberDrawn);

        

        // Both parts must be complete and cell must be in pattern
        return middleRowComplete && middleColumnComplete && isInPattern;
      }
      
      case "4 Corners Square": {
        // Four corners: (0,0), (0,4), (4,0), (4,4)
        const corners = [
          [0, 0], [0, 4], [4, 0], [4, 4]
        ];
        
        const isInPattern = corners.some(([r, c]) => r === rowIndex && c === colIndex);
        
        if (!isInPattern) {
          return false;
        }
        
        // Check if all four corners are complete
        const allCornersComplete = corners.every(([r, c]) => {
          const cellNumber = cartella.numbers[r][c];
          return cellNumber && drawnNumbers.includes(cellNumber);
        });
        
        return allCornersComplete && isInPattern;
      }
      
      default:
    
        return false;
    }
  };

  if (!cartella || !cartella.numbers) {
  
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      <Box sx={modalStyle}>
        <Typography
          variant='h6'
          align='left'
          sx={{ color: "black", fontWeight: "light" }}
        >
          Bingo
        </Typography>

        {/* Title Bar */}
        <Box
          sx={{
            bgcolor: "#0066cc",
            mx: 0,
            mt: 0,
            mb: 2,
            p: 1,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
          }}
        >
          {testMode && (
            <Box
              sx={{
                position: "absolute",
                top: 5,
                right: 10,
                bgcolor: "#ffeb3b",
                color: "#000",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.7rem",
                fontWeight: "bold",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 0.7 },
                  "50%": { opacity: 1 },
                  "100%": { opacity: 0.7 },
                },
              }}
            >
              TEST MODE
            </Box>
          )}

          <Typography
            variant='h4'
            align='center'
            sx={{ color: "white", fontWeight: "bold" }}
          >
            Card No: {cartellaNumber}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showBonusSection}
                  onChange={(e) => setShowBonusSection(e.target.checked)}
                  sx={{
                    color: "white",
                    "&.Mui-checked": {
                      color: "white",
                    },
                  }}
                />
              }
              label='Show Bonus Options'
              sx={{
                color: "white",
                ".MuiFormControlLabel-label": {
                  fontSize: "0.9rem",
                  fontWeight: "medium",
                },
              }}
            />
          </Box>
        </Box>

        {/* BINGO Header */}
        <Box
          sx={{
            display: "table",
            width: "90%",
            margin: "0 auto",
            borderSpacing: 0,
            borderCollapse: "collapse",
            mb: 0,
          }}
        >
          <Box sx={{ display: "table-row" }}>
            {["B", "I", "N", "G", "O"].map((letter, i) => (
              <Box
                key={letter}
                sx={{
                  display: "table-cell",
                  width: "20%",
                  background:
                    i === 0
                      ? "linear-gradient(135deg, #000066 0%, #0099ff 100%)"
                      : i === 1
                      ? "#990000"
                      : i === 2
                      ? "#009933"
                      : i === 3
                      ? "#996600"
                      : "linear-gradient(135deg, #666666 0%, #999999 100%)",
                  p: 1,
                  textAlign: "center",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  align='center'
                  sx={{
                    fontSize: "2.5rem",
                    fontWeight: "bold",
                    color: "white",
                    lineHeight: 1,
                  }}
                >
                  {letter}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Numbers Grid */}
        <Box
          sx={{
            display: "table",
            width: "90%",
            margin: "0 auto",
            borderSpacing: 0,
            borderCollapse: "collapse",
            minHeight: "55vh",
          }}
        >
          {cartella.numbers.map((row, i) => (
            <Box key={i} sx={{ display: "table-row", height: "22px" }}>
              {row.map((number, j) => {
                const isMiddleCell = i === 2 && j === 2;
                const isDrawn = isNumberDrawn(number);
                const isWinningLine = isWinningNumber(number, i, j);

                let backgroundColor = "transparent";
                let color = "black";

                if (isWinningLine) {
                  if (winningPattern === "Mark") {
                    backgroundColor = "green";
                  } else {
                    backgroundColor = "blue";
                  }
                  color = "white";
                } else if (isDrawn) {
                  backgroundColor = "red";
                  color = "white";
                }

                // The middle cell is always drawn. It should be blue if it's part of a winning line (e.g., 'X', 'T Cross'),
                // but just red for patterns where it's not explicitly part of the win (like 'Mark').
                if (isMiddleCell) {
                  if (isWinningLine && winningPattern !== "Mark") {
                    backgroundColor = "blue";
                  } else {
                    backgroundColor = "red"; // It's always drawn
                  }
                  color = "white";
                }

                return (
                  <Box
                    key={`${i}-${j}`}
                    sx={{
                      display: "table-cell",
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: "20%",
                      position: "relative",
                      padding: 0,
                      border: "1px solid #bdbdbd",
                      backgroundColor: "#eeeeee",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: isMiddleCell ? "0.9rem" : "2.5rem",
                        fontWeight: "bold",
                        backgroundColor,
                        color,
                        borderRadius: "50%",
                        zIndex: 1,
                      }}
                    >
                      {isMiddleCell ? "free" : number}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Bonus Check Section */}
        {showBonusSection && (
          <Box
            sx={{
              mt: 2,
              mb: 3,
              mx: "auto",
              width: "90%",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              position: "relative",
            }}
          >
            <Divider sx={{ mb: 1 }}>
              <Typography
                variant='subtitle1'
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  color: "#790918",
                }}
              >
                Check for Bonus {testMode && "🧪 TEST MODE"}
              </Typography>
            </Divider>
            
            {testMode && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffeaa7",
                  borderRadius: 1,
                  textAlign: "center"
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#856404",
                    fontWeight: "bold",
                    fontSize: "0.9rem"
                  }}
                >
                  🧪 TEST MODE ACTIVE - Bonuses will be saved to reports for testing!
                  <br />
                  <span style={{ fontSize: "0.8rem", fontWeight: "normal" }}>
                    Press Alt+B to toggle test mode
                  </span>
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel
                  id='bonus-type-label'
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    backgroundColor: "white",
                    px: 0.5,
                    color: "#000000",
                  }}
                >
                  Bonus Type
                </InputLabel>
                <Select
                  labelId='bonus-type-label'
                  value={selectedBonus}
                  label='Bonus Type'
                  onChange={(e) => setSelectedBonus(e.target.value)}
                  sx={{
                    minWidth: 150,
                    height: 45,
                    color: "white",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#790918",
                      borderWidth: 2,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#790918",
                      borderWidth: 2,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#790918",
                      borderWidth: 2,
                    },
                    "& .MuiSelect-select": {
                      fontSize: "1rem",
                      fontWeight: "medium",
                      color: "#000000",
                    },
                  }}
                >
                  <MenuItem value='' sx={{ color: "white" }}>
                    <em>None</em>
                  </MenuItem>
                  <MenuItem value='anyOneLine' sx={{ color: "white" }}>
                    አንድ ዝግ (One Line)
                  </MenuItem>
                  <MenuItem value='anyTwoLines' sx={{ color: "white" }}>
                    ሁለት ዝግ (Two Lines)
                  </MenuItem>
                  <MenuItem value='cheers' sx={{ color: "white" }}>
                    ቺርስ (CHEERS)
                  </MenuItem>
                </Select>
              </FormControl>

              <Button
                variant='contained'
                disabled={!selectedBonus || isLoading}
                onClick={handleCheckBonus}
                sx={{
                  minWidth: 120,
                  height: 45,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  backgroundColor: "#790918",
                  "&:hover": {
                    backgroundColor: "#5d0713",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Check Bonus"
                )}
              </Button>
            </Box>

            {bonusResult && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: bonusResult.success
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(211, 47, 47, 0.1)",
                  border: `1px solid ${
                    bonusResult.success
                      ? "rgba(46, 125, 50, 0.5)"
                      : "rgba(211, 47, 47, 0.5)"
                  }`,
                  position: "relative",
                  mb: 1,
                }}
              >
                <Button
                  onClick={() => setBonusResult(null)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    minWidth: "auto",
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    p: 0,
                    color: bonusResult.success ? "#2e7d32" : "#d32f2f",
                    "&:hover": {
                      backgroundColor: bonusResult.success
                        ? "rgba(46, 125, 50, 0.1)"
                        : "rgba(211, 47, 47, 0.1)",
                    },
                  }}
                >
                  ✕
                </Button>

                <Typography
                  variant='h6'
                  sx={{
                    color: bonusResult.success ? "#2e7d32" : "#d32f2f",
                    fontWeight: "bold",
                    mb: 1,
                    pr: 4, // Make room for the close button
                  }}
                >
                  {bonusResult.message}
                </Typography>

                <Typography variant='body2' sx={{ mb: 1, color: "#000000" }}>
                  {bonusResult.details}
                </Typography>

                {bonusResult.success && (
                  <Typography
                    variant='h5'
                    sx={{ fontWeight: "bold", color: "#2e7d32" }}
                  >
                    Prize: {bonusResult.prize.toLocaleString()} ETB
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 1,
            mt: 0,
            mx: "auto",
            width: "100%",
          }}
        >
          <Button
            variant='contained'
            onClick={handleGoodBingo}
            sx={{
              bgcolor: "#009933",
              "&:hover": { bgcolor: "#008029" },
              fontSize: "0.9rem",
              textTransform: "none",
              border: "2px solid #006622",
              borderRadius: 0,
              fontWeight: "bolder",
            }}
          >
            GOOD BINGO
          </Button>
          <Button
            variant='contained'
            onClick={handleNotBingo}
            sx={{
              bgcolor: "#800000",
              "&:hover": { bgcolor: "#660000" },
              fontSize: "0.9rem",
              textTransform: "none",
              border: "2px solid #4d0000",
              borderRadius: 0,
              fontWeight: "bolder",
            }}
          >
            NOT BINGO
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              setIsLoading(true);
              onAdditional();
              setTimeout(() => setIsLoading(false), 1000);
            }}
            disabled={isLoading}
            sx={{
              bgcolor: "#009933",
              "&:hover": { bgcolor: "#008029" },
              fontSize: "1rem",
              textTransform: "none",
              border: "2px solid #006622",
              borderRadius: 0,
              fontWeight: "bolder",
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "black" }} />
            ) : (
              "Additional"
            )}
          </Button>
          <Button
            variant='contained'
            onClick={() => {
              setIsLoading(true);
              onNewBingo();
              setTimeout(() => setIsLoading(false), 1000);
            }}
            disabled={isLoading}
            sx={{
              bgcolor: "#0000ff",
              "&:hover": { bgcolor: "#0000cc" },
              fontSize: "0.9rem",
              textTransform: "none",
              border: "2px solid #0000cc",
              borderRadius: 0,
              fontWeight: "bolder",
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: "black" }} />
            ) : (
              "NEW BINGO"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CartellaCheckModal;
